import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { NoddiButton } from 'noddi-ui-web';
import { BookingDetailsTabs, customerRoutes } from 'noddi-util';
import { useNavigate } from 'react-router-dom';
import { BookingValidationDialog } from '../Common/BookingValidationDialog';

export type StationaryBookingValidationVariant =
  | 'carSelectionRequired'
  | 'serviceSelectionRequired'
  | 'tireHotelSeasonRequired'
  | 'timeSlotRequired'
  | 'stepIncomplete'
  | 'existingBooking'
  | 'existingColleagueBooking'
  | 'existingBookingProposal';

interface StationaryBookingValidationModalProps {
  open: boolean;
  onClose: () => void;
  variant: StationaryBookingValidationVariant;
  body?: Record<string, unknown>;
}

const getValidationMessage = (variant: StationaryBookingValidationVariant) => {
  switch (variant) {
    case 'carSelectionRequired':
      return {
        title: t`Car selection required`,
        message: t`Please select one car before proceeding to the next step.`
      };
    case 'serviceSelectionRequired':
      return {
        title: t`Service selection required`,
        message: t`Please select a service before proceeding to the next step.`
      };
    case 'tireHotelSeasonRequired':
      return {
        title: t`Choose which tires we store`,
        message: t`Choose winter or summer tires before you continue.`
      };
    case 'timeSlotRequired':
      return {
        title: t`Time slot required`,
        message: t`Please select one of the available time slots for your appointment before proceeding.`
      };
    case 'existingBooking':
      return {
        title: t`Existing booking found`,
        message: t`This car already has an upcoming booking. Complete or cancel it before creating a new one.`
      };
    // A booking the caller may not open (a colleague's, seen by a plain organization member):
    // they can neither go to it nor cancel it, so the way forward is the organization's administrator.
    case 'existingColleagueBooking':
      return {
        title: t`Existing booking found`,
        message: t`This car already has an upcoming booking made by someone else in your organization. Contact your organization's administrator if it needs to change.`
      };
    case 'existingBookingProposal':
      return {
        title: t`Existing booking proposal found`,
        message: t`This car already has a pending booking proposal. Confirm it instead of creating a new booking.`
      };
    case 'stepIncomplete':
      return {
        title: t`Step incomplete`,
        message: t`Please complete all required fields before proceeding to the next step.`
      };
  }
};

export const StationaryBookingValidationModal = ({
  open,
  onClose,
  variant,
  body
}: StationaryBookingValidationModalProps) => {
  const navigate = useNavigate();
  const { title, message } = getValidationMessage(variant);
  const firstUpcomingBookingSlug = body?.firstUpcomingBookingSlug as string | undefined;
  const firstPendingProposalSlug = body?.firstPendingProposalSlug as string | undefined;

  return (
    <BookingValidationDialog
      open={open}
      onClose={onClose}
      title={title}
      message={message}
      actions={
        <>
          {variant === 'existingBooking' && firstUpcomingBookingSlug && (
            <NoddiButton
              variant='outline'
              onClick={() => {
                navigate(
                  customerRoutes.bookingInfo.getPath({
                    slug: firstUpcomingBookingSlug,
                    tab: BookingDetailsTabs.ORDER_DETAILS
                  })
                );
                onClose();
              }}
            >
              <Trans>Go to booking</Trans>
            </NoddiButton>
          )}
          {variant === 'existingBookingProposal' && firstPendingProposalSlug && (
            <NoddiButton
              variant='outline'
              onClick={() => {
                navigate(customerRoutes.bookingProposalLandingPage.getPath({ slug: firstPendingProposalSlug }));
                onClose();
              }}
            >
              <Trans>Go to booking proposal</Trans>
            </NoddiButton>
          )}
        </>
      }
    />
  );
};
